import React from "react";
import DeliveryDateConfigSection from "../_shared/DeliveryDateConfigSection";
import ThresholdSettingsSection from "../_shared/ThresholdSettingsSection";
import TileEventAttributeFilter from "../LiefertermintreueKPI/components/TileEventAttributeFilter";
import LocalFilterPanel from "../../../components/common/LocalFilterPanel";

const AGGREGATION_OPTIONS = [
  { value: "min", label: "Minimum" },
  { value: "median", label: "Median" },
  { value: "average", label: "Durchschnitt" },
  { value: "max", label: "Maximum" },
  { value: "sum", label: "Summe" },
];

const ROLLING_WINDOW_OPTIONS = [7, 14, 30];

const AbsoluteDelayTimelineSettings = ({
  componentTitle,
  onComponentTitleChange,
  objectType,
  onObjectTypeChange,
  objectTypes,
  refDateType,
  onRefDateTypeChange,
  refDateValue,
  onRefDateValueChange,
  refEventAttributeSource,
  onRefEventAttributeSourceChange,
  actualDateType,
  onActualDateTypeChange,
  actualDateValue,
  onActualDateValueChange,
  actualEventAttributeSources,
  onActualEventAttributeSourcesChange,
  getAttributesForActivity,
  activitiesForType,
  dateAttrsForType,
  activitiesLoading,
  dateAttrsLoading,
  tileEventAttributeFilter,
  onTileEventAttributeFilterChange,
  aggregation,
  onAggregationChange,
  includeMode,
  onIncludeModeChange,
  rollingWindowDays,
  onRollingWindowDaysChange,
  thresholdType,
  onThresholdTypeChange,
  thresholdTarget,
  onThresholdTargetChange,
  thresholdWarningPercentage,
  onThresholdWarningPercentageChange,
  thresholdLower,
  onThresholdLowerChange,
  thresholdUpper,
  onThresholdUpperChange,
  localFilters,
  updateLocalFilter,
  loading = false,
  activeFile,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
        <input
          type="text"
          value={componentTitle}
          onChange={(e) => onComponentTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Absoluter Verzug über Zeit"
          disabled={loading}
        />
      </div>

      <DeliveryDateConfigSection
        objectType={objectType}
        onObjectTypeChange={onObjectTypeChange}
        objectTypes={objectTypes}
        refDateType={refDateType}
        onRefDateTypeChange={onRefDateTypeChange}
        refDateValue={refDateValue}
        onRefDateValueChange={onRefDateValueChange}
        refEventAttributeSource={refEventAttributeSource}
        onRefEventAttributeSourceChange={onRefEventAttributeSourceChange}
        actualDateType={actualDateType}
        onActualDateTypeChange={onActualDateTypeChange}
        actualDateValue={actualDateValue}
        onActualDateValueChange={onActualDateValueChange}
        actualEventAttributeSources={actualEventAttributeSources}
        onActualEventAttributeSourcesChange={onActualEventAttributeSourcesChange}
        getAttributesForActivity={getAttributesForActivity}
        activitiesForType={activitiesForType}
        dateAttrsForType={dateAttrsForType}
        loading={loading}
        activitiesLoading={activitiesLoading}
        dateAttrsLoading={dateAttrsLoading}
      />

      <div className="border-t border-gray-200" />

      <TileEventAttributeFilter
        value={tileEventAttributeFilter}
        onChange={onTileEventAttributeFilterChange}
        activeFile={activeFile}
        disabled={loading}
      />

      <div className="border-t border-gray-200" />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Aggregation</label>
        <div className="flex flex-wrap gap-2">
          {AGGREGATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={loading}
              onClick={() => onAggregationChange(opt.value)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                aggregation === opt.value
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className={aggregation === "sum" ? "opacity-50 pointer-events-none" : ""}>
        <label className="block text-sm font-medium text-gray-700 mb-2">Berücksichtigte Aufträge</label>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={loading || aggregation === "sum"}
            onClick={() => onIncludeModeChange("late_only")}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              includeMode === "late_only"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            Nur verspätete
          </button>
          <button
            type="button"
            disabled={loading || aggregation === "sum"}
            onClick={() => onIncludeModeChange("all")}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              includeMode === "all"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            Alle (pünktliche = 0 Tage)
          </button>
        </div>
        {aggregation === "sum" && (
          <p className="text-xs text-amber-600 mt-1">
            Bei "Summe" hat diese Auswahl keinen Effekt: pünktliche Aufträge tragen 0 Tage Verzug
            bei, ändern die Summe also nicht — "Alle" und "Nur verspätete" ergeben denselben Wert.
          </p>
        )}
      </div>

      <div className={aggregation === "median" ? "opacity-50 pointer-events-none" : ""}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Glättungsfenster (Rolling Average)
        </label>
        <div className="flex gap-2">
          {ROLLING_WINDOW_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              disabled={loading || aggregation === "median"}
              onClick={() => onRollingWindowDaysChange(n)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                rollingWindowDays === n
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {n} Tage
            </button>
          ))}
        </div>
        {aggregation === "median" ? (
          <p className="text-xs text-amber-600 mt-1">
            Glättung bei Median nicht möglich: ein "Median der Tages-Mediane" ist statistisch
            etwas anderes als der echte Median über den Zeitraum und kann irreführend sein.
            Bei Median wird daher nur die rohe Linie angezeigt.
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">
            Die Tile zeigt zusätzlich zur rohen Linie eine geglättete Linie (trailing
            {" "}{rollingWindowDays}-Tage-Fenster) — sinnvoll bei wenigen Aufträgen pro Tag.
          </p>
        )}
      </div>

      <ThresholdSettingsSection
        thresholdType={thresholdType}
        onThresholdTypeChange={onThresholdTypeChange}
        thresholdTarget={thresholdTarget}
        onThresholdTargetChange={onThresholdTargetChange}
        thresholdWarningPercentage={thresholdWarningPercentage}
        onThresholdWarningPercentageChange={onThresholdWarningPercentageChange}
        thresholdLower={thresholdLower}
        onThresholdLowerChange={onThresholdLowerChange}
        thresholdUpper={thresholdUpper}
        onThresholdUpperChange={onThresholdUpperChange}
        loading={loading}
      />

      <div className="border-t border-gray-200" />

      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['dateRange', 'activityNodes', 'objectAttributes']}
      />
    </div>
  );
};

export default AbsoluteDelayTimelineSettings;
